/* eslint-disable import/first */
import _ from "lodash";
import moment from "moment";
import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Form } from "react-bootstrap";
import { unstable_batchedUpdates } from "react-dom";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { MdClose, MdContentCopy } from "react-icons/md";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import AudioReactRecorder, { RecordState } from "audio-react-recorder";
import { callAPI, callAPI2 } from "../../../helper/apiUtils";
import { cleanTranscript, isInvalidTranscript } from "../../../utils/conversationUtils";
import { handleComposerEnterKey } from "../../../utils/composerUtils";
import {
  BASE_URL,
  KJUUP_COLORS,
  getSenderColor,
} from "../../../helper/constants";
import { $crud } from "../../../helper/CrudFactory";
import { $user } from "../../../helper/UserFactory";
import {
  DRAFT_MESSAGES,
  GROUPS,
  OPEN_CHATS,
  PROJECTS,
  GROUP_CHATS,
  FORWARD_GROUP_MESSAGES,
  ALL_BADGE_COUNT,
  UPDATE_GROUP_MESSAGE,
} from "../../../store";
import "../../../styles/pages.css";
import "./KjuupPilotChat.css";
import InfiniteScroll from "react-infinite-scroll-component";
import { sumTotalChatHistory } from "../../../helper/SocketObj";
import LanguageContext from "../../../helper/LanguageContext";
import { urlify } from "../../../helper/convertor";
import { Oval } from "react-loader-spinner";
import { getUsernameFromText } from "./getUsername";
import { MentionsInput, Mention } from "react-mentions";
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import remarkBreaks from 'remark-breaks';
import { marked } from 'marked';
import { FiSearch } from "react-icons/fi";

/** Module level on purpose: react-markdown keys its output off these functions,
 *  so rebuilding the object per render makes React remount the message instead
 *  of updating it, which drops any text the reader has selected. */
const PILOT_MARKDOWN_COMPONENTS = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>
  ),
  h1: ({ children }) => <span className="markdown-header-h1-h4 markdown-header-colored">{children}</span>,
  h2: ({ children }) => <span className="markdown-header-h1-h4 markdown-header-colored">{children}</span>,
  h3: ({ children }) => <span className="markdown-header-h1-h4 markdown-header-colored">{children}</span>,
  h4: ({ children }) => <span className="markdown-header-h1-h4 markdown-header-colored">{children}</span>,
  h5: ({ children }) => <span className="markdown-header-h5-h6">{children}</span>,
  h6: ({ children }) => <span className="markdown-header-h5-h6">{children}</span>,
  ul: ({ children }) => <ul className="markdown-ul-medium">{children}</ul>,
};
const PILOT_REMARK_PLUGINS = [remarkGfm, remarkBreaks];
const PILOT_REHYPE_PLUGINS = [rehypeRaw];

// =====================================================================
// Isolated input component — typing only re-renders this, not the
// entire message list with its heavy Markdown parsing.
// =====================================================================

const MENTIONS_INPUT_STYLE = {
  control: { backgroundColor: 'transparent', fontSize: "var(--kjuup-type-body)", fontWeight: 'normal', minHeight: 20 },
  '&multiLine': {
    control: { minHeight: 20 },
    highlighter: { padding: 0, border: 'none' },
    input: { padding: 0, border: 'none', outline: 'none' },
  },
  suggestions: {
    zIndex: 99999,
    backgroundColor: 'white',
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      borderRadius: 10,
      maxHeight: 300,
      overflowY: 'auto',
    },
    item: {
      padding: '10px 15px',
      borderBottom: '1px solid rgba(0,0,0,0.1)',
      cursor: 'pointer',
      '&focused': { backgroundColor: 'var(--kjuup-bg)' },
    },
  },
};

const MAX_MENTIONS_INPUT_HEIGHT = 160;

const mentionDisplayTransform = (id, display) => `@${display}`;

const renderUserSuggestion = (mention) => (
  <div
    key={mention.id}
    className="kjuup-pilot-chat__suggestion"
  >
    <div>
      {mention.type !== "All" ? (
        <img
          src={
            mention.avatar
              ? BASE_URL + mention.avatar
              : process.env.PUBLIC_URL + "/assets/img/contact-us.svg"
          }
          className="kjuup-pilot-chat__suggestion-avatar"
          alt=""
        />
      ) : (
        <span
          className="material-symbols-outlined kjuup-pilot-chat__suggestion-group-icon"
        >
          groups
        </span>
      )}
    </div>
    <div className="kjuup-pilot-chat__suggestion-name">
      {mention.name}
    </div>
  </div>
);

const PilotChatInput = React.memo(function PilotChatInput({
  chatId,
  isMessageSending,
  isGerm,
  isTranscribing,
  mentionSuggestions,
  onSend,
  onVoiceSend,
  initialMessage,
  voiceTranscriptError,
  onDismissVoiceError,
  pendingVoiceTranscript,
  onVoiceTopicChoice,
  onVoiceTopicDismiss,
}) {
  const [message, setMessage] = useState(initialMessage || "");
  const [showTopicPrompt, setShowTopicPrompt] = useState(false);
  const [editedVoiceTranscript, setEditedVoiceTranscript] = useState("");
  const [audioRecording, setAudioRecording] = useState(false);
  const [recordState, setRecordState] = useState(RecordState.STOP);
  const [recordingCounter, setRecordingCounter] = useState(0);
  const textRef = useRef(null);
  const messageRef = useRef(message);
  const pendingMessageRef = useRef("");
  const recordingStartRef = useRef(0);
  messageRef.current = message;

  useLayoutEffect(() => {
    const textarea = textRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    const minHeight = 20;
    const nextHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), MAX_MENTIONS_INPUT_HEIGHT);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > MAX_MENTIONS_INPUT_HEIGHT ? 'auto' : 'hidden';
  }, [message]);

  useEffect(() => {
    if (pendingVoiceTranscript) {
      setEditedVoiceTranscript(pendingVoiceTranscript);
    }
  }, [pendingVoiceTranscript]);

  useEffect(() => {
    let interval;
    if (audioRecording) {
      interval = setInterval(() => {
        setRecordingCounter((c) => c + 1);
      }, 1000);
    }
    if (!audioRecording) {
      setRecordingCounter(0);
    }
    return () => clearInterval(interval);
  }, [audioRecording]);

  const handleInitiateSend = useCallback(() => {
    const val = messageRef.current;
    if (val) {
      pendingMessageRef.current = val;
      setShowTopicPrompt(true);
    }
  }, []);

  const handleTopicChoice = useCallback((isNewTopic) => {
    const val = pendingMessageRef.current;
    if (val) {
      onSend(val, isNewTopic);
      setMessage("");
      pendingMessageRef.current = "";
    }
    setShowTopicPrompt(false);
  }, [onSend]);

  const handleDismissPrompt = useCallback(() => {
    setShowTopicPrompt(false);
    pendingMessageRef.current = "";
    if (textRef.current) textRef.current.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    const textarea = textRef.current || e.target;
    const outcome = handleComposerEnterKey(e, {
      textValue: textarea.value ?? message,
      selectionStart: textarea.selectionStart,
      onListReplace: (nextValue, nextCursor) => {
        setMessage(nextValue);
        requestAnimationFrame(() => {
          const el = textRef.current;
          if (el && el.setSelectionRange) el.setSelectionRange(nextCursor, nextCursor);
        });
      },
    });
    if (outcome === "send") {
      handleInitiateSend();
    }
  }, [handleInitiateSend, message]);

  const handleChange = useCallback((e) => {
    setMessage(e.target.value);
  }, []);

  const handleFocusClick = useCallback(() => {
    if (textRef.current) textRef.current.focus();
  }, []);

  const startVoiceRecording = useCallback(() => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      if (!navigator.getUserMedia && !navigator.webkitGetUserMedia) {
        return;
      }
    }
    recordingStartRef.current = Date.now();
    setAudioRecording(true);
    setRecordState(RecordState.START);
  }, []);

  const stopVoiceRecording = useCallback(() => {
    setRecordState(RecordState.STOP);
  }, []);

  const cancelVoiceRecording = useCallback(() => {
    setRecordState(RecordState.STOP);
    setAudioRecording(false);
  }, []);

  const handleRecordingStop = useCallback((audioData) => {
    const elapsed = Date.now() - recordingStartRef.current;
    if (audioData && audioData.blob && audioData.blob.size > 100 && elapsed > 500 && audioRecording) {
      const $fileName = new Date().getTime() + ".mp3";
      const audioFile = new File([audioData.blob], $fileName, {
        lastModified: new Date().getTime(),
        type: "audio/mp3",
      });
      onVoiceSend(audioFile);
    }
    setAudioRecording(false);
  }, [audioRecording, onVoiceSend]);

  const isBusy = isMessageSending || isTranscribing;

  return (
    <div
      className={`chat_input p-0 kjuup-pilot-chat__input ${isBusy ? 'pilot-input-sending' : ''}`}
    >
      {isBusy && <div className="pilot-input-progress" />}

      <AudioReactRecorder
        style={{ height: 0 }}
        canvasHeight={0}
        canvasWidth={0}
        state={recordState}
        onStop={handleRecordingStop}
      />

      {showTopicPrompt && (
        <div className="kjuup-pilot-chat__prompt kjuup-pilot-chat__center-kids">
          <span className="kjuup-pilot-chat__prompt-text">
            {isGerm ? 'Ist das ein neues Thema?' : 'Is this a new topic?'}
          </span>
          <button
            onClick={() => handleTopicChoice(true)}
            className="kj-btn kj-btn--primary kj-btn--sm"
          >
            {isGerm ? 'Ja, neues Thema' : 'Yes, new topic'}
          </button>
          <button
            onClick={() => handleTopicChoice(false)}
            className="kj-btn kj-btn--outline kj-btn--on-surface kj-btn--sm"
          >
            {isGerm ? 'Gleiches Thema' : 'Same topic'}
          </button>
          <span
            onClick={handleDismissPrompt}
            className="kjuup-pilot-chat__prompt-close"
          >
            <MdClose size={18} />
          </span>
        </div>
      )}

      {voiceTranscriptError && (
        <div className="kjuup-pilot-chat__voice-error kjuup-pilot-chat__center-kids">
          <span className="kjuup-pilot-chat__voice-error-text">
            {voiceTranscriptError}
          </span>
          <span
            onClick={onDismissVoiceError}
            className="kjuup-pilot-chat__voice-error-close"
          >
            <MdClose size={18} />
          </span>
        </div>
      )}

      {pendingVoiceTranscript && (
        <div className="kjuup-pilot-chat__transcript">
          <div className="kjuup-pilot-chat__transcript-field">
            <label className="kjuup-pilot-chat__transcript-label">
              {isGerm ? 'Transkription (bearbeitbar):' : 'Transcript (editable):'}
            </label>
            <textarea
              value={editedVoiceTranscript}
              onChange={(e) => setEditedVoiceTranscript(e.target.value)}
              className="kjuup-pilot-chat__transcript-textarea"
              onFocus={(e) => { e.target.style.borderColor = KJUUP_COLORS.base_color; }}
              onBlur={(e) => { e.target.style.borderColor = '#ddd'; }}
            />
          </div>
          <div className="kjuup-pilot-chat__transcript-actions kjuup-pilot-chat__center-kids">
            <span className="kjuup-pilot-chat__prompt-text">
              {isGerm ? 'Ist das ein neues Thema?' : 'Is this a new topic?'}
            </span>
            <button
              onClick={() => onVoiceTopicChoice(true, editedVoiceTranscript.trim())}
              disabled={!editedVoiceTranscript.trim()}
              className="kj-btn kj-btn--primary kj-btn--sm"
            >
              {isGerm ? 'Ja, neues Thema' : 'Yes, new topic'}
            </button>
            <button
              onClick={() => onVoiceTopicChoice(false, editedVoiceTranscript.trim())}
              disabled={!editedVoiceTranscript.trim()}
              className="kj-btn kj-btn--outline kj-btn--on-surface kj-btn--sm"
            >
              {isGerm ? 'Gleiches Thema' : 'Same topic'}
            </button>
            <span
              onClick={onVoiceTopicDismiss}
              className="kjuup-pilot-chat__prompt-close"
            >
              <MdClose size={18} />
            </span>
          </div>
        </div>
      )}

      {audioRecording ? (
        <div
          className="pilot-recording-container kjuup-pilot-chat__recording kjuup-pilot-chat__center-kids"
        >
          <a
            className="recording-cancel-icon"
            href="#"
            onClick={(e) => { e.preventDefault(); cancelVoiceRecording(); }}
          >
            <span className="material-symbols-outlined">close</span>
          </a>
          <div className="kj-flex-1-min">
            <div className="kjuup-pilot-chat__recording-track">
              <div
                className="kjuup-pilot-chat__recording-fill"
                style={{ '--progress': `${Math.min(recordingCounter, 100)}%` }}
              />
            </div>
          </div>
          <span className="kjuup-pilot-chat__recording-time">
            {new Date(recordingCounter * 1000).toISOString().substr(14, 5)}
          </span>
          <a className="recording-send-icon pointer" href="#" onClick={(e) => { e.preventDefault(); stopVoiceRecording(); }}>
            <span className="material-symbols-outlined">send</span>
          </a>
        </div>
      ) : (
        <div
          className={`kjuup-pilot-chat__input-row kjuup-pilot-chat__center-kids${(isBusy || showTopicPrompt) ? ' kjuup-pilot-chat__input-row--disabled' : ''}`}
        >
          <div
            className={`input_text_msg customInputBoxNew kjuup-pilot-chat__input-box${isBusy ? ' kjuup-pilot-chat__input-box--busy' : ''}`}
            onClick={isBusy ? undefined : handleFocusClick}
          >
            <Form.Group controlId="exampleForm.ControlInput1">
              <MentionsInput
                className="mentions scrollable-mentions"
                value={message}
                disabled={isBusy || showTopicPrompt}
                onKeyDown={handleKeyDown}
                onChange={handleChange}
                inputRef={textRef}
                allowSuggestionsAboveCursor
                forceSuggestionsAboveCursor
                id={`text-area-${chatId}`}
                suggestionsPortalHost={document.body}
                style={MENTIONS_INPUT_STYLE}
              >
                <Mention
                  markup={"@[__display__](__id__)"}
                  trigger="@"
                  data={mentionSuggestions}
                  displayTransform={mentionDisplayTransform}
                  renderSuggestion={renderUserSuggestion}
                />
              </MentionsInput>
            </Form.Group>
          </div>

          {message && !isBusy && (
            <a
              href="javascript:void(0);"
              onClick={handleInitiateSend}
              className="kjuup-pilot-chat__send-link"
            >
              <span className="material-symbols-outlined send-icon kjuup-pilot-chat__send-icon">send</span>
            </a>
          )}

          {!message && !isBusy && (
            <a
              href="javascript:void(0);"
              onClick={startVoiceRecording}
              className="kjuup-pilot-chat__mic-link"
            >
              <span className="material-symbols-outlined kjuup-pilot-chat__mic-icon">mic</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
});

function KjuupPilotChat(props) {
  const user = $user.get();
  const { isGerm, strings } = useContext(LanguageContext);

  // --- Core state ---
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isGettingChats, setIsGettingChats] = useState(false);
  const [showChatLoadingPopup, setShowChatLoadingPopup] = useState(true);
  const [isMessageSending, setIsMessageSending] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState([]);
  const [pendingVoiceTranscript, setPendingVoiceTranscript] = useState(null);
  const [voiceTranscriptError, setVoiceTranscriptError] = useState(null);
  let [tempChatHistory, setTempChatHistory] = useState([]);
  const [allMsgs, setAllMsgs] = useState([]);
  const [hoveredChatId, setHoveredChatId] = useState(null);
  const [expandedMessages, setExpandedMessages] = useState({});
  const [mentionSuggestions, setMentionSuggestions] = useState([]);

  // --- Search state ---
  const [searchView, setSearchView] = useState(false);
  const [search, setSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // --- Refs ---
  const messagesEndRef = useRef(null);
  const chatIdTrackingRef = useRef(null);
  const isLoadingRef = useRef(false);

  // =====================================================================
  // MESSAGE GROUPING
  // =====================================================================

  const groupMessagesByDate = (messages) => {
    if (!messages || messages.length === 0) return [];
    const chatData = messages.map((msg) => ({
      ...msg,
      originalMessage: msg.message,
      messageLng: props.language ? props.language : "en",
    }));
    const sorted = _.orderBy(chatData, [(m) => new Date(m.createdAt), (m) => m.id || 0], ["asc", "asc"]);
    const grouping = _.groupBy(sorted, (el) =>
      moment(el.createdAt).format("D MMM, Y")
    );
    let sections = _.map(grouping, (items, createdAt) => ({ createdAt, items }));
    sections = _.orderBy(sections, [(obj) => new Date(obj.createdAt)], ["desc"]);
    return sections;
  };

  // =====================================================================
  // MESSAGE TRUNCATION / EXPANSION
  // =====================================================================

  const toggleMessageExpansion = (messageId) => {
    setExpandedMessages((prev) => ({ ...prev, [messageId]: !prev[messageId] }));
  };

  const renderTruncatedMessage = (messageContent, messageId, className = "other-message") => {
    const MAX_LENGTH = 800;
    const isExpanded = expandedMessages[messageId];
    const needsTruncation = messageContent && messageContent.length > MAX_LENGTH;

    let processedContent = messageContent || "";

    if (searchQuery && messageId && isMessageInSearchResults(messageId)) {
      processedContent = addSearchHighlighting(processedContent, searchQuery);
    }

    const finalContent = urlify(getUsernameFromText(processedContent, strings));

    if (!needsTruncation || isExpanded) {
      return (
        <span
          className={`${className} kjuup-pilot-chat__msg-text`}
          dangerouslySetInnerHTML={{ __html: finalContent }}
        />
      );
    }

    const truncatedContent = processedContent.substring(0, MAX_LENGTH);
    const truncatedFinalContent = urlify(getUsernameFromText(truncatedContent, strings));

    return (
      <>
        <span
          className={`${className} kjuup-pilot-chat__msg-text`}
          dangerouslySetInnerHTML={{ __html: truncatedFinalContent }}
        />
        <span className="mehr-button" onClick={() => toggleMessageExpansion(messageId)}>
          ...Mehr
        </span>
      </>
    );
  };

  // =====================================================================
  // CHAT HISTORY & PAGINATION
  // =====================================================================

  const _getMessages = async (pageNum = 1) => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setIsGettingChats(true);

    try {
      const { data } = await callAPI(
        `/kjuupPilot/chat/history?user=${user.id}&page=${pageNum}&limit=30`,
        {},
        "GET"
      );
      const history = data?.history || [];

      if (history.length > 0) {
        const processedMessages = history.map((e) => {
          const dateValue = e.createdAt ? new Date(e.createdAt) : new Date();
          const createdAt = isNaN(dateValue.getTime()) ? new Date().toISOString() : dateValue.toISOString();
          return {
            ...e,
            createdAt,
            message: e.content || e.message || '',
            senderId: e.role === 'user' ? user.id : '',
            messageType: "simple",
          };
        }).reverse();

        unstable_batchedUpdates(() => {
          setAllMsgs((prev) => [...history, ...prev]);

          if (pageNum === 1) {
            const sections = groupMessagesByDate(processedMessages);
            setTempChatHistory([{ id: props.chat?.id, data: sections }]);
          } else {
            setTempChatHistory((prev) => {
              const existingItems = prev?.[0]?.data?.flatMap(s => s.items) || [];
              const existingIds = new Set(existingItems.map(item => item.id).filter(Boolean));
              const newItems = processedMessages.filter(msg => !existingIds.has(msg.id));
              const allItems = [...newItems, ...existingItems];
              const sections = groupMessagesByDate(allItems);
              return [{ id: props.chat?.id, data: sections }];
            });
          }

          setIsGettingChats(false);
          if (pageNum === 1) setShowChatLoadingPopup(false);
          if (history.length < 30) setHasMore(false);
        });

        if (pageNum === 1) setTimeout(() => scrollToBottom(), 100);
      } else {
        unstable_batchedUpdates(() => {
          if (pageNum === 1) {
            setTempChatHistory([{ id: props.chat?.id, data: [] }]);
          }
          setIsGettingChats(false);
          setHasMore(false);
          if (pageNum === 1) setShowChatLoadingPopup(false);
        });
      }
    } catch (error) {
      console.error('Error loading messages:', error);
      unstable_batchedUpdates(() => {
        setIsGettingChats(false);
        setShowChatLoadingPopup(false);
      });
    } finally {
      isLoadingRef.current = false;
    }
  };

  const fetchMoreData = () => {
    if (!isGettingChats && hasMore && !searchView) {
      const finalPage = page + 1;
      setPage((p) => p + 1);
      _getMessages(finalPage);
    }
  };

  // =====================================================================
  // LIFECYCLE EFFECTS
  // =====================================================================

  const draftMessage = useMemo(() => {
    const msg = props.draftMessages.find(
      (m) => m.userId === user?.id && m.group === props.chat?.id
    );
    return msg?.message || "";
  }, [props.chat?.id]);

  useEffect(() => {
    if (chatIdTrackingRef.current !== null && chatIdTrackingRef.current === props.chat?.id) {
      return;
    }
    chatIdTrackingRef.current = props.chat?.id;

    setPage(1);
    setHasMore(true);
    setAllMsgs([]);
    getDetails(props.chat?.id);
  }, [props.chat?.id]);

  useEffect(() => {
    if (props.chat?.id) {
      _getMessages(1);
    }
  }, [props.chat?.id]);


  // =====================================================================
  // SCROLL
  // =====================================================================

  const scrollToBottom = () => {
    const container = document.getElementById(`chat-sc-${props.chat?.id}`);
    if (container) {
      container.scrollTop = 0;
    }
  };

  // =====================================================================
  // SEND MESSAGE
  // =====================================================================

  const addThinkingStep = useCallback((textDe, textEn) => {
    setThinkingSteps((prev) => [...prev, { de: textDe, en: textEn, ts: Date.now() }]);
  }, []);

  const sendMessage = useCallback(async (messageText, isNewTopic = false) => {
    setIsMessageSending(true);
    setThinkingSteps([]);
    addThinkingStep("Nachricht wird vorbereitet…", "Preparing your message…");

    let messagex = messageText;

    let converText = messagex
      .replace(/<br\s*[/]?>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/&nbsp;/g, " ");

    let msg = {
      building: props.projectDetails?.id,
      message: converText,
      senderId: user?.id,
      sender: user.id,
      messageType: "simple",
      newTopic: isNewTopic,
    };

    const msgWithDate = { ...msg, createdAt: new Date().toISOString() };
    const todayDate = moment().format("D MMM, Y");

    setTempChatHistory((prev) => {
      if (prev.length > 0 && prev[0]?.data?.[0]?.items) {
        const existingItems = prev[0].data.flatMap(s => s.items);
        const allItems = [...existingItems, msgWithDate];
        const sections = groupMessagesByDate(allItems);
        return [{ id: prev[0].id, data: sections }];
      }
      return [{ id: props.chat?.id, data: [{ createdAt: todayDate, items: [msgWithDate] }] }];
    });

    addThinkingStep("Anfrage wird an Kjuup Pilot AI gesendet…", "Sending request to Kjuup Pilot AI…");

    try {
      addThinkingStep("Kjuup Pilot AI verarbeitet Ihre Anfrage…", "Kjuup Pilot AI is processing your request…");

      const thinkingInterval = setInterval(() => {
        const extras = [
          ["Kontext wird analysiert…", "Analyzing context…"],
          ["Relevante Informationen werden abgerufen…", "Retrieving relevant information…"],
          ["Quellen werden ausgewertet…", "Evaluating sources…"],
          ["Antwort wird formuliert…", "Formulating response…"],
          ["Ergebnisse werden zusammengestellt…", "Compiling results…"],
          ["Daten werden strukturiert…", "Structuring data…"],
          ["Fachbegriffe werden geprüft…", "Verifying terminology…"],
        ];
        const pick = extras[Math.floor(Math.random() * extras.length)];
        setThinkingSteps((prev) => [...prev, { de: pick[0], en: pick[1], ts: Date.now() }]);
      }, 3000);

      const response = await callAPI("/kjuupPilot/chat/completion", {}, "POST", msg);
      clearInterval(thinkingInterval);

      addThinkingStep("Antwort erhalten – wird verarbeitet…", "Response received – processing…");

      const responseData = response?.data;
      const messagesArray = Array.isArray(responseData) ? responseData :
        (Array.isArray(responseData?.data) ? responseData.data :
          (Array.isArray(responseData?.history) ? responseData.history : null));

      if (messagesArray && messagesArray.length > 0) {
        const newMessages = messagesArray.map((e) => {
          const dateValue = e.createdAt ? new Date(e.createdAt) : new Date();
          const createdAt = isNaN(dateValue.getTime()) ? new Date().toISOString() : dateValue.toISOString();
          return { ...e, createdAt, message: e.content || e.message || '', senderId: e.role === 'user' ? user.id : '', messageType: "simple" };
        });

        addThinkingStep("Antwort wird angezeigt ✓", "Displaying response ✓");

        setTempChatHistory((prev) => {
          const existingItems = prev?.[0]?.data?.flatMap(s => s.items) || [];
          const filteredItems = existingItems.filter((e) => e.message !== converText || e.id);
          const newIds = new Set(newMessages.map(m => m.id).filter(Boolean));
          const deduped = filteredItems.filter(e => !e.id || !newIds.has(e.id));
          const allItems = [...deduped, ...newMessages];
          const sections = groupMessagesByDate(allItems);
          return [{ id: props.chat?.id, data: sections }];
        });

        setTimeout(() => scrollToBottom(), 100);
      }
    } catch (error) {
      console.error("[KjuupPilotChat] Error sending message:", error);
      addThinkingStep("Fehler aufgetreten ✗", "Error occurred ✗");
    } finally {
      setTimeout(() => {
        setIsMessageSending(false);
        setThinkingSteps([]);
      }, 600);
    }
  }, [props.projectDetails?.id, props.chat?.id, user?.id, addThinkingStep]);

  // =====================================================================
  // VOICE → TRANSCRIBE → SEND
  // =====================================================================

  const handleVoiceSend = useCallback(async (audioFile) => {
    setIsTranscribing(true);
    setVoiceTranscriptError(null);
    setThinkingSteps([]);
    addThinkingStep("Sprachnachricht wird transkribiert…", "Transcribing voice message…");

    try {
      const formData = new FormData();
      formData.append("file", audioFile, audioFile.name);

      const transcriptRes = await callAPI2(
        "/kjuupPilot/transcribeAudio", {}, "POST", formData, { timeout: 120000 },
        { "Content-Type": "multipart/form-data" }
      );

      const respData = transcriptRes?.data;

      if (respData?.status === false || respData?.data?.status === false) {
        setIsTranscribing(false);
        setThinkingSteps([]);
        setVoiceTranscriptError(
          isGerm
            ? "Ihre Sprachnachricht konnte leider nicht transkribiert werden. Bitte versuchen Sie es erneut."
            : "Sorry, your voice message could not be transcribed. Please try again."
        );
        return;
      }

      const rawTranscript = respData?.transcript
        || respData?.data?.transcript
        || respData?.transcribedAudio
        || respData?.data?.transcribedAudio
        || respData?.text
        || respData?.data?.text
        || (typeof respData === 'string' ? respData : null);

      const transcript = cleanTranscript(rawTranscript);

      if (!transcript || isInvalidTranscript(transcript)) {
        setIsTranscribing(false);
        setThinkingSteps([]);
        setVoiceTranscriptError(
          isGerm
            ? "Ihre Sprachnachricht konnte leider nicht transkribiert werden. Bitte versuchen Sie es erneut."
            : "Sorry, your voice message could not be transcribed. Please try again."
        );
        return;
      }

      addThinkingStep("Transkription erfolgreich ✓", "Transcription successful ✓");
      setIsTranscribing(false);
      setThinkingSteps([]);
      setPendingVoiceTranscript(transcript);
    } catch (error) {
      console.error("[KjuupPilotChat] Voice transcription error:", error);
      setIsTranscribing(false);
      setThinkingSteps([]);
      setVoiceTranscriptError(
        isGerm
          ? "Fehler bei der Sprachtranskription. Bitte versuchen Sie es erneut."
          : "Voice transcription failed. Please try again."
      );
    }
  }, [props.chat?.id, addThinkingStep, isGerm]);

  const handleVoiceTopicChoice = useCallback((isNewTopic, editedText) => {
    const transcript = editedText || pendingVoiceTranscript;
    setPendingVoiceTranscript(null);
    if (transcript && transcript.trim()) {
      sendMessage(transcript.trim(), isNewTopic);
    }
  }, [pendingVoiceTranscript, sendMessage]);

  const handleVoiceTopicDismiss = useCallback(() => {
    setPendingVoiceTranscript(null);
  }, []);

  // =====================================================================
  // COPY
  // =====================================================================

  const handleCopy = (chat) => {
    const copyMsg = chat?.message || "";
    const htmlContent = marked.parse(copyMsg);
    const htmlBlob = new Blob([htmlContent], { type: "text/html" });
    const textBlob = new Blob([copyMsg], { type: "text/plain" });
    const CItem = window["ClipboardItem"];
    navigator.clipboard.write([
      new CItem({ "text/html": htmlBlob, "text/plain": textBlob })
    ]).then(() => {
      sessionStorage.setItem('copiedFromKjuupPilot', Date.now().toString());
      $crud.notify({ type: "success", message: "Copied!" });
    });
  };

  // =====================================================================
  // SEARCH
  // =====================================================================

  const performSearch = async (query, pageNum = 1) => {
    if (!query || query.trim() === "") {
      setSearchResults([]);
      setTotalSearchResults(0);
      setCurrentSearchIndex(0);
      return;
    }
    setIsSearching(true);
    try {
      const { data } = await callAPI(
        `/kjuupPilot/chat/history?page=${pageNum}&limit=30&query=${encodeURIComponent(query.trim())}`,
        {},
        "GET"
      );
      if (data?.history && Array.isArray(data.history) && data.history.length > 0) {
        const resultsWithPage = data.history.map((msg) => ({
          ...msg,
          id: msg.id,
          content: msg.content || msg.message || "",
          page_no: msg.page_no || pageNum,
          createdAt: msg.createdAt,
          role: msg.role,
        }));
        if (pageNum === 1) {
          setSearchResults(resultsWithPage);
          setTotalSearchResults(resultsWithPage.length);
          setCurrentSearchIndex(0);
        } else {
          setSearchResults((prev) => [...prev, ...resultsWithPage]);
          setTotalSearchResults((prev) => prev + resultsWithPage.length);
        }
      } else if (pageNum === 1) {
        setSearchResults([]);
        setTotalSearchResults(0);
        setCurrentSearchIndex(0);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
      setTotalSearchResults(0);
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedSearch = useCallback(
    _.debounce((query) => {
      if (query && query.trim() !== "") {
        setSearchQuery(query);
        performSearch(query, 1);
      } else {
        setSearchQuery("");
        setSearchResults([]);
        setTotalSearchResults(0);
        setCurrentSearchIndex(0);
        setIsSearching(false);
      }
    }, 1000),
    []
  );

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearch(query);
    debouncedSearch(query);
  };

  const clearSearch = () => {
    setSearch("");
    setSearchQuery("");
    setSearchResults([]);
    setTotalSearchResults(0);
    setCurrentSearchIndex(0);
    setSearchView(false);
    setIsSearching(false);
    if (props.chat?.id) _getMessages(1);
  };

  const navigateToNextResult = async () => {
    if (currentSearchIndex < totalSearchResults - 1) {
      await navigateToResult(currentSearchIndex + 1);
    }
  };

  const navigateToPreviousResult = async () => {
    if (currentSearchIndex > 0) {
      await navigateToResult(currentSearchIndex - 1);
    }
  };

  const navigateToResult = async (index) => {
    if (index < 0 || index >= totalSearchResults || !searchResults[index]) return;
    const result = searchResults[index];
    if (!result?.id) return;

    const messagePage = result.page_no || 1;
    const messageId = result.id;
    setCurrentSearchIndex(index);

    const messageExists = allMsgs.some((msg) => String(msg.id) === String(messageId));
    if (!messageExists) {
      await _getMessages(messagePage);
      setTimeout(() => scrollToMessage(messageId), 1000);
    } else {
      setTimeout(() => scrollToMessage(messageId), 100);
    }
  };

  const scrollToMessage = (messageId) => {
    let messageElement = document.getElementById(`chat-${messageId}`);
    if (!messageElement) {
      messageElement = document.querySelector(`[data-message-id="${messageId}"]`);
    }
    if (messageElement) {
      messageElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      const scrollContainer = document.getElementById(`chat-scroll-view-${props.chat?.id}`);
      if (scrollContainer) {
        setTimeout(() => {
          const containerRect = scrollContainer.getBoundingClientRect();
          const messageRect = messageElement.getBoundingClientRect();
          const offsetFromTop = messageRect.top - containerRect.top;
          scrollContainer.scrollTo({
            top: scrollContainer.scrollTop + offsetFromTop - (containerRect.height / 2),
            behavior: "smooth",
          });
        }, 100);
      }
      setTimeout(() => {
        const currentElement = document.getElementById(`chat-${messageId}`);
        if (!currentElement) return;
        document.querySelectorAll('.search-result-highlight').forEach((el) => {
          el.classList.remove('search-result-highlight');
        });
        const bubbleElement = currentElement.querySelector('.user_info_chat') ||
          currentElement.closest('.user_info_chat') ||
          currentElement;
        bubbleElement.classList.add('search-result-highlight');
        setTimeout(() => bubbleElement.classList.remove('search-result-highlight'), 3000);
      }, 300);
    }
  };

  const isMessageInSearchResults = (messageId) => {
    if (!searchQuery || searchResults.length === 0) return false;
    return searchResults.some((result) => String(result.id) === String(messageId) || result.id === messageId);
  };

  const addSearchHighlighting = (text, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === "" || !text) return text;
    try {
      const escapedSearch = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escapedSearch})`, "gi");
      return text.replace(regex, '<mark style="background-color: #FFEB3B; padding: 2px 0; border-radius: 2px; color: inherit;">$1</mark>');
    } catch (error) {
      return text;
    }
  };

  useEffect(() => {
    if (searchResults.length > 0 && searchQuery) {
      setTimeout(() => {
        if (currentSearchIndex === 0) navigateToResult(0);
      }, 500);
    }
  }, [searchResults.length, searchQuery]);

  // =====================================================================
  // MEMOIZED MESSAGE SECTIONS (avoids re-grouping on every keystroke)
  // =====================================================================

  const renderedSections = useMemo(() => {
    const items = tempChatHistory
      ?.filter((item) => item?.id === props.chat?.id)?.[0]
      ?.data?.flatMap((s) => s.items) || [];
    const grouped = groupMessagesByDate(items) || [];
    return [...grouped].reverse();
  }, [tempChatHistory, props.chat?.id]);

  // =====================================================================
  // MARKDOWN RENDERING
  // =====================================================================

  const checkForMarkdownContailsOrNot = (message) => {
    return getUsernameFromText(message?.trim(), strings);
  };

  const _renderCommonMarkDownCmp = (msg, messageId = null) => {
    const MAX_LENGTH = 800;
    let processedMessage = checkForMarkdownContailsOrNot(msg);

    if (searchQuery && messageId && isMessageInSearchResults(messageId)) {
      processedMessage = addSearchHighlighting(processedMessage, searchQuery);
    }

    const isExpanded = messageId ? expandedMessages[messageId] : true;
    const needsTruncation = messageId && processedMessage && processedMessage.length > MAX_LENGTH;
    const displayMessage = (needsTruncation && !isExpanded)
      ? processedMessage.substring(0, MAX_LENGTH)
      : processedMessage;

    return (
      <>
        <Markdown
          rehypePlugins={PILOT_REHYPE_PLUGINS}
          remarkPlugins={PILOT_REMARK_PLUGINS}
          skipHtml={false}
          components={PILOT_MARKDOWN_COMPONENTS}
        >
          {displayMessage}
        </Markdown>
        {needsTruncation && !isExpanded && (
          <span className="mehr-button" onClick={() => toggleMessageExpansion(messageId)}>
            ...Mehr
          </span>
        )}
      </>
    );
  };

  // =====================================================================
  // GROUP DETAILS (for mentions)
  // =====================================================================

  const getDetails = async (chatId) => {
    try {
      if (!user) return;
      const { data } = await callAPI(`/group/${chatId}/info`, {}, "GET");
      if (data.status) {
        let mems = data.groupInfo.users
          .filter((u) => u.id !== user?.id && !u.isLeave)
          .map((e) => ({ ...e, name: e.fullName, display: e.fullName }));
        let xcx = [...mems];
        if (mems.length > 0) {
          xcx.unshift({ name: strings.All, display: strings.All, id: "idall12322", type: "All" });
        }
        setMentionSuggestions([...xcx]);
      }
    } catch (error) {
      console.error("Error getting details:", error);
    }
  };

  // =====================================================================
  // DRAFT MESSAGES
  // =====================================================================

  const clearDraftMessage = async () => {
    let allMessages = props.draftMessages.filter((msg) => msg.group !== props.chat?.id);
    localStorage.setItem("draftMessage", JSON.stringify(allMessages));
    props.updateDraftMessages(allMessages);
  };

  const preserveChatHistory = () => {
    const chatHistoryObj = { userId: user?.id, group: props.chat?.id, messages: [] };
    let chatHistories = localStorage.getItem("chatHistories")
      ? JSON.parse(localStorage.getItem("chatHistories"))
      : [];
    const foundIndex = chatHistories.findIndex(
      (msg) => msg.userId === user?.id && msg.group === props.chat?.id
    );
    if (foundIndex > -1) {
      chatHistories[foundIndex] = { ...chatHistories[foundIndex], messages: [] };
    } else {
      chatHistories = [...chatHistories, chatHistoryObj];
    }
    localStorage.setItem("chatHistories", JSON.stringify(chatHistories));
  };

  // =====================================================================
  // THINKING INDICATOR (inline chat bubble)
  // =====================================================================

  const ThinkingBubble = () => {
    const visibleSteps = thinkingSteps.slice(-5);
    const latestStep = thinkingSteps[thinkingSteps.length - 1];

    return (
      <div className="kjuup-pilot-chat__msg-row">
        <div className="kjuup-pilot-chat__msg-gutter" />
        <div className="forword-conver del-other kjuup-pilot-chat__msg-body">
          <div>
            <div className="user_chat_content">
              <div>
                <div className="kjuup-pilot-chat__msg-align">
                  <div>
                    <div
                      className="user_info_chat left_users pb-1 pilot-thinking-bubble kjuup-pilot-chat__thinking"
                    >
                      <div className="kjuup-pilot-chat__thinking-steps">
                        {visibleSteps.map((step, i) => {
                          const isLatest = i === visibleSteps.length - 1;
                          const text = isGerm ? step.de : step.en;
                          return (
                            <div
                              key={step.ts}
                              className={`kjuup-pilot-chat__step kjuup-pilot-chat__center-kids ${isLatest ? "pilot-step-current" : "pilot-step-done kjuup-pilot-chat__step--done"}`}
                            >
                              {isLatest
                                ? <div className="pilot-thinking-spinner" />
                                : <span className="kjuup-pilot-chat__step-check">✓</span>
                              }
                              <span className={`kjuup-pilot-chat__step-text${isLatest ? ' kjuup-pilot-chat__step-text--current' : ''}`}>
                                {text}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =====================================================================
  // MESSAGE RENDERING
  // =====================================================================

  const renderMessage = (chat, index) => {
    if (chat.role === 'separator') {
      let text = chat?.message || chat?.content || '';
      text = String(text).replace(/"/g, '').replace(/'/g, '').replace(/\*+/g, '');
      const parenIndex = text.search(/[\(（]/);
      if (parenIndex !== -1) text = text.substring(0, parenIndex).trim();
      return (
        <div key={index} className="no_member_chat pilot-topic-separator">
          <span className="pilot-topic-text">{text}</span>
        </div>
      );
    }

    const isAssistant = chat.senderId !== user?.id;

    return (
      <div
        key={index}
        className="kjuup-pilot-chat__msg-row"
      >
        <div className="kjuup-pilot-chat__msg-gutter" />
        <div
          className={isAssistant ? "forword-conver del-other kjuup-pilot-chat__msg-body" : "forword-conver kjuup-pilot-chat__msg-body"}
        >
          <div>
            <div
              className={isAssistant ? "user_chat_content" : "user_chat_content right_users"}
            >
              <div>
                <div className={isAssistant ? "kjuup-pilot-chat__msg-align" : "kjuup-pilot-chat__msg-align kjuup-pilot-chat__msg-align--end"}>
                  <div>
                    <div
                      className={isAssistant ? "user_info_chat left_users pb-1 kjuup-pilot-chat__bubble kjuup-pilot-chat__bubble--assistant" : "user_info_chat left_users pb-1 kjuup-pilot-chat__bubble"}
                      id={`chat-${chat?.id}`}
                      onMouseEnter={() => isAssistant && setHoveredChatId(chat?.id)}
                      onMouseLeave={() => isAssistant && setHoveredChatId(null)}
                    >
                      {isAssistant && hoveredChatId === chat?.id && (
                        <button
                          className="pilot-copy-btn kj-btn kj-btn--icon kj-btn--sm kj-btn--neutral kj-btn--on-surface"
                          onClick={() => handleCopy(chat)}
                          title={strings.copy}
                        >
                          <MdContentCopy size={20} />
                        </button>
                      )}
                      {isAssistant && (
                        <p className="mb-1">
                          <span
                            className="sender-name"
                            style={{ "--sender-color": chat.themeColor || getSenderColor(chat.senderId) }}
                          >
                            {chat.senderName}
                          </span>
                        </p>
                      )}
                      {chat?.messageType === "simple" && (
                        <p className={`m-0 simple-msg-kjuup-pilot`}>
                          {isAssistant
                            ? _renderCommonMarkDownCmp(chat?.message, chat?.id)
                            : renderTruncatedMessage(chat?.message, chat?.id, "user-message")
                          }
                        </p>
                      )}
                      <div className="clearfix" />
                      <div className="small">
                        {moment(chat.createdAt).format("HH:mm")}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // =====================================================================
  // RENDER
  // =====================================================================

  return (
    <React.Fragment>
      <div
        className="w-300 chat_text chat_box_main_div pilot-chat-container kjuup-pilot-chat"
      >
        {/* Loading Skeleton */}
        {showChatLoadingPopup && (
          <div className="chat-loading-overlay kjuup-pilot-chat__skeleton">
            {[0, 1, 2].map((groupIndex) => (
              <div key={groupIndex} className="kjuup-pilot-chat__skeleton-group">
                <div className="kjuup-pilot-chat__skeleton-date-row">
                  <div
                    className="kj-skeleton-pulse kjuup-pilot-chat__skeleton-date"
                    style={{ '--delay': `${groupIndex * 0.1}s` }}
                  />
                </div>
                {[0, 1, 2, 3].map((msgIndex) => (
                  <div
                    key={msgIndex}
                    className={msgIndex % 2 === 0 ? 'kjuup-pilot-chat__skeleton-row' : 'kjuup-pilot-chat__skeleton-row kjuup-pilot-chat__skeleton-row--end'}
                  >
                    <div
                      className={`kj-skeleton-pulse kjuup-pilot-chat__skeleton-bubble${msgIndex % 3 === 0 ? ' kjuup-pilot-chat__skeleton-bubble--w200' : msgIndex % 3 === 1 ? ' kjuup-pilot-chat__skeleton-bubble--w150' : ''}${msgIndex % 2 === 0 ? ' kjuup-pilot-chat__skeleton-bubble--left' : ''}`}
                      style={{ '--delay': `${(groupIndex * 0.1) + (msgIndex * 0.05)}s` }}
                    />
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* Header */}
        <div className="chat_header kjuup-pilot-chat__header kjuup-pilot-chat__center-kids">
          <div className="row kjuup-pilot-chat__header-row">
            <div className="col-md-12 kjuup-pilot-chat__header-col">
              <div className="client_name kjuup-pilot-chat__header-inner kjuup-pilot-chat__center-kids">
                <div onClick={() => props.handleClose()} className="kjuup-pilot-chat__back">
                  <label className="back_icon tabs_mob_view kjuup-pilot-chat__back-label">
                    <span className={`material-symbols-outlined material-arrow-left kjuup-pilot-chat__back-icon ${props.project?.isInternal ? 'internal-arrow' : ''}`}>
                      keyboard_arrow_left
                    </span>
                  </label>
                </div>
                <img className="img_active kjuup-pilot-chat__logo"
                  src={process.env.PUBLIC_URL + "assets/img/PilotLogo.png"} alt="" />

                {/* Search Bar */}
                {searchView && (
                  <div
                    className={`pilot-search-bar kjuup-pilot-chat__search kjuup-pilot-chat__center-kids ${isSearching ? 'pilot-search-active' : ''}`}
                  >
                    <div className="kjuup-pilot-chat__search-icon kjuup-pilot-chat__center-kids">
                      {isSearching ? (
                        <Oval
                          height={16}
                          width={16}
                          color={KJUUP_COLORS.base_color || "#737373"}
                          visible={true}
                          ariaLabel="search-loading"
                          secondaryColor="#c4c4c4"
                          strokeWidth={4}
                          strokeWidthSecondary={4}
                        />
                      ) : (
                        <FiSearch size={16} />
                      )}
                    </div>

                    <input
                      type="text"
                      placeholder={isGerm ? "Suchen" : "Search"}
                      value={search}
                      onChange={handleSearch}
                      disabled={isSearching}
                      className={`kjuup-pilot-chat__search-input${isSearching ? ' kjuup-pilot-chat__search-input--busy' : ''}`}
                      autoFocus
                    />

                    {searchQuery && totalSearchResults > 0 && (
                      <>
                        <div className="kjuup-pilot-chat__search-count">
                          {currentSearchIndex + 1} {isGerm ? "von" : "of"} {totalSearchResults}
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigateToNextResult(); }}
                          disabled={currentSearchIndex >= totalSearchResults - 1}
                          className="kj-btn kj-btn--icon kj-btn--sm kjuup-pilot-chat__search-nav"
                        >
                          <BsChevronUp size={16} />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); navigateToPreviousResult(); }}
                          disabled={currentSearchIndex === 0}
                          className="kj-btn kj-btn--icon kj-btn--sm kjuup-pilot-chat__search-nav"
                        >
                          <BsChevronDown size={16} />
                        </button>
                      </>
                    )}

                    <div
                      onClick={(e) => { e.stopPropagation(); clearSearch(); }}
                      className="kjuup-pilot-chat__search-clear kjuup-pilot-chat__center-kids"
                      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--kjuup-accent)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#737373"; }}
                    >
                      <MdClose size={20} />
                    </div>
                  </div>
                )}

                {/* Title + Search Icon */}
                {!searchView && (
                  <div
                    className={
                      props.chat?.unseenChatCount > 0
                        ? "clinet_name_text w-100 pilot-header-title kjuup-pilot-chat__title kjuup-pilot-chat__center-kids"
                        : "clinet_name_text pl-3 w-100 pilot-header-title kjuup-pilot-chat__title kjuup-pilot-chat__center-kids"
                    }
                  >
                    <h5 className="kjuup-pilot-chat__title-text">Kjuup Pilot</h5>
                    <div
                      onClick={() => {
                        setSearchView(true);
                        setSearch("");
                        setSearchQuery("");
                        setSearchResults([]);
                        setTotalSearchResults(0);
                        setCurrentSearchIndex(0);
                      }}
                      className="kjuup-pilot-chat__search-open kjuup-pilot-chat__center-kids"
                      onMouseEnter={(e) => { e.currentTarget.style.color = "var(--kjuup-accent)"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#737373"; }}
                    >
                      <FiSearch size={18} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Message List */}
        <div className="kjuup-pilot-chat__list">
          <div
            className={
              sumTotalChatHistory(tempChatHistory) < 4
                ? "bottom-bar chat-scroll-first kjuup-pilot-chat__list-inner"
                : "bottom-bar chat-scroll kjuup-pilot-chat__list-inner"
            }
            id={`chat-scroll-view-${props.chat?.id}`}
          >
            <div
              className="wscroll kjuup-pilot-chat__scroll"
              ref={messagesEndRef}
              id={`chat-sc-${props.chat?.id}`}
            >
              <InfiniteScroll
                dataLength={tempChatHistory?.length ? tempChatHistory[0].data.length : 0}
                next={() => fetchMoreData()}
                className="pilot-chat-infinite-scroll"
                inverse={true}
                hasMore={hasMore}
                loader={
                  isGettingChats && page > 1 ? (
                    <div className="kjuup-pilot-chat__loader">
                      <Oval
                        height={30}
                        width={30}
                        color={KJUUP_COLORS.base_color}
                        visible={true}
                        ariaLabel="loading"
                        secondaryColor="#c4c4c4"
                        strokeWidth={5}
                        strokeWidthSecondary={5}
                      />
                    </div>
                  ) : <></>
                }
                scrollableTarget={`chat-sc-${props.chat?.id}`}
                scrollThreshold={1}
                onScroll={(e) => {
                  const bottom = e.target.scrollHeight + e.target.scrollTop === e.target.clientHeight;
                  if (bottom && hasMore && !isGettingChats) fetchMoreData();
                }}
              >
                {isMessageSending && <ThinkingBubble />}
                {renderedSections
                  .map((section, sindex) => {
                    if (!section) return null;
                    return (
                      <div key={sindex} className="kjuup-pilot-chat__section">
                        <div className="date_chat">
                          <p>{section?.createdAt}</p>
                        </div>
                        {section?.items?.map((chat, index) => renderMessage(chat, index))}
                      </div>
                    );
                  })}
              </InfiniteScroll>
            </div>
          </div>
        </div>

        {/* Input Area */}
        <PilotChatInput
          key={props.chat?.id}
          chatId={props.chat?.id}
          isMessageSending={isMessageSending}
          isTranscribing={isTranscribing}
          isGerm={isGerm}
          mentionSuggestions={mentionSuggestions}
          onSend={sendMessage}
          onVoiceSend={handleVoiceSend}
          initialMessage={draftMessage}
          voiceTranscriptError={voiceTranscriptError}
          onDismissVoiceError={() => setVoiceTranscriptError(null)}
          pendingVoiceTranscript={pendingVoiceTranscript}
          onVoiceTopicChoice={handleVoiceTopicChoice}
          onVoiceTopicDismiss={handleVoiceTopicDismiss}
        />
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => {
  const {
    openChats,
    groups,
    updateGroupMessage,
    language,
    groupChats,
    forwardGroupMessages,
    draftMessages,
    updateProjectActionFire,
  } = state;
  return {
    groups,
    language,
    openChats,
    updateGroupMessage,
    groupChats,
    forwardGroupMessages,
    draftMessages,
    updateProjectActionFire,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    updateGroups: (groups) => dispatch({ type: GROUPS, groups }),
    updateProjects: (projects) => dispatch({ type: PROJECTS, projects }),
    updateOpenChats: (chats) => dispatch({ type: OPEN_CHATS, openChats: chats }),
    updateOpenChatsList: (chats) => dispatch({ type: OPEN_CHATS, openChats: chats }),
    updateLastMessage: (msg) => dispatch({ type: UPDATE_GROUP_MESSAGE, updateGroupMessage: msg }),
    updateGroupChats: (chats) => dispatch({ type: GROUP_CHATS, groupChats: chats }),
    updateForwardGroupMessage: (groupMessages) => dispatch({ type: FORWARD_GROUP_MESSAGES, forwardGroupMessages: groupMessages }),
    updateDraftMessages: (msg) => dispatch({ type: DRAFT_MESSAGES, draftMessages: msg }),
    updateAllBadgeCount: (count) => dispatch({ type: ALL_BADGE_COUNT, unSeenAllCount: count }),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(memo(KjuupPilotChat)));
